"use client";
import VerifiedIcon from "@/assets/icons/verified.svg";
import TelegramIcon from "@/assets/icons/logo-telegram-colored.svg";
import TwitterIcon from "@/assets/icons/logo-twitter.svg";
// import DiscordIcon from "@/assets/icons/logo-discord.svg";
import { Address } from "@/app/(components)/Basic/Address";
import Link from "next/link";
import styles from "./ProfileHeader.module.css";
import DefaultCover from "@/app/(components)/Basic/DefaultCover/DefaultCover";
import { WithThemeAndBlockchain } from "@/shared/types";
import { Theme } from "@/shared/constants";
import { ArtistFollowButton } from "../../Artists/ArtistFollowButton";
import { User } from "@/api/users/types";
import { ArtistTipModalButton } from "../../Artists/ArtistTipModalButton";
import {
  getUsersHref,
  isUserVerified,
  isUserArtist,
} from "@/api/users/helpers";
import { EndemicImage } from "@/app/(components)/Basic/Image";
import { Profile } from "../types";
import { UserImage } from "@/app/(components)/Basic/UserImage";
import { getUsersName } from "@/api/users/helpers";
import { getProfileName } from "../helpers";
import { ProfileImage } from "../ProfileImage";
import { Rating } from "@/app/(components)/Basic/Rating";
import { Blockchain } from "@/shared/config/blockchain/types";
import { RequestCookie } from "next/dist/compiled/@edge-runtime/cookies";
import { OpenToButton } from "../../OpenToWork/OpenToButton";
import { HireArtistButton } from "../../OpenToWork/HireArtistButton";

export interface Props {
  profile: Profile;
  blockchain: Blockchain;
  theme?: Theme | RequestCookie;
  isUserFollowing?: boolean;
  isArtistMe?: boolean;
  isUserLoggedIn?: boolean;
  showProfileOwner?: boolean;
  showProfileVerified?: boolean;
  showProfileAddress?: boolean;
  showArtistActions?: boolean;
  showArtistRating?: boolean;
}

const ProfileHeader = (props: Props) => {
  return <ProfileHeaderBase {...props} theme={props.theme as Theme} />;
};

const ProfileHeaderBase = ({
  profile,
  blockchain,
  theme,
  isUserFollowing,
  isArtistMe,
  isUserLoggedIn,
  showProfileOwner,
  showProfileVerified,
  showProfileAddress,
  showArtistActions,
  showArtistRating,
}: WithThemeAndBlockchain<Props>) => {
  return (
    <header className="max-w-[1280px]">
      <section className="flex gap-x-[200px] lg:gap-x-[184px] relative lg:rounded-3xl w-full h-36 lg:h-96 overflow-hidden">
        {/** solution for cover image border */}
        <div className="z-10 h-full w-full lg:w-[54px] lg:rounded-l-3xl border-grey endemic:border-white border-y lg:border-l border-right-0"></div>
        <div className="z-10 h-full w-full lg:rounded-r-3xl border-grey endemic:border-white border-y lg:border-r border-right-0"></div>
        <div className="z-10 absolute top-0 w-52 left-1/2 -translate-x-1/2 lg:-translate-x-0 lg:left-[42px] border-grey endemic:border-white border-t"></div>
        {profile.headerImageUrl ? (
          <EndemicImage
            src={profile.headerImageUrl}
            placeholder={profile.headerImagePlaceholder}
            alt={`${profile.displayName}'s cover image`}
            className={`object-cover ${styles.mask}`}
            fill
            priority
          />
        ) : (
          <DefaultCover className={styles.mask} theme={theme} />
        )}
      </section>
      <section className="flex flex-col items-center lg:items-start lg:flex-row pt-5">
        <ProfileImage
          profile={profile}
          className={"-mt-[106px] lg:-mt-[60px]"}
        />

        <div className="lg:ml-7 text-center lg:text-start flex flex-col items-center lg:items-start">
          <div className="font-semibold tracking-tighter text-3xl lg:text-5xl text-primary mb-2  lg:w-min xl:w-auto">
            {getProfileName(profile)}
          </div>
          <div className="text-lg leading-5 mb-2 text-secondary flex items-center">
            {profile.displayName ? `@${profile.displayName}` : "unnamed"}
            {showProfileVerified &&
              isUserVerified(profile as User, blockchain) && (
                <VerifiedIcon className="w-[22px] h-[22px] fill-[#1C82EA] ml-2" />
              )}
          </div>
        </div>
        <div className="pt-3 lg:ml-auto flex flex-col lg:flex-row gap-y-5 items-center">
          {showArtistRating && profile.avgRating && (
            <div className="inline-flex lg:mr-2">
              <Rating
                rating={profile.avgRating}
                starClassName="mr-[2px]"
                useHalfStars
                locked
              />
            </div>
          )}
          {showProfileAddress && profile.address && (
            <div className="inline-flex lg:mr-2">
              <Address address={profile.address} />
            </div>
          )}
          {showProfileOwner && profile.owner && (
            <Link
              href={getUsersHref(profile.owner)}
              className="flex items-center justify-center lg:w-auto bg-endemicGray-50 lg:border-4 rounded-3xl border-white shadow-md h-10 lg:mr-2"
            >
              <UserImage
                image={{ src: profile.owner.imageUrl }}
                imagePlaceholder={profile.owner.imagePlaceholder}
                imgClassName="w-[32px] h-[32px]"
                small
                className="flex items-center justify-center"
              />
              <div className="bg-gray pr-4 text-left hidden lg:block">
                <div className="font-bold text-[13px] leading-[1.2]">
                  {getUsersName(profile.owner, true)}
                </div>
                <div className="text-[11px] leading-[1.2] font-light text-endemicGray">
                  {profile.owner.displayName}
                </div>
              </div>
            </Link>
          )}
          {(profile.telegram || profile.twitter || profile.discord) && (
            <div className="inline-flex gap-2 lg:mr-6">
              {profile.telegram && (
                <Link
                  href={`tg://user?id=${profile.telegram}`}
                  className="cursor-pointer shadow-md rounded-full w-8 h-8 inline-flex items-end justify-center border-4 border-[--color-bg-primary] box-content bg-[#1877F2]"
                  target="_blank"
                >
                  <TelegramIcon className="w-8 fill-white" />
                </Link>
              )}
              {profile.twitter && (
                <Link
                  href={`https://www.twitter.com/${profile.twitter}`}
                  className="cursor-pointer shadow-md rounded-full w-8 h-8 inline-flex items-center justify-center border-4 border-[--color-bg-primary] box-content bg-[#1CB7EB]"
                  target="_blank"
                >
                  <TwitterIcon className="w-5 fill-white" />
                </Link>
              )}
              {/** TODO: fix discord link */}
              {/* {profile.discord && (
              <Link
                href={`https://www.discord.com/${profile.discord}`}
                className="cursor-pointer shadow-md rounded-full w-8 h-8 inline-flex items-center justify-center border-4 border-[--color-bg-primary] box-content bg-[#8C9EFF]"
                target="_blank"
              >
                <DiscordIcon className="w-5 fill-white" />
              </Link>
            )} */}
            </div>
          )}
        </div>
      </section>

      <section className="pb-8 pt-6 lg:pt-2 border-b lg:pl-[244px] flex gap-2 md:gap-4 justify-center lg:justify-start">
        {isArtistMe && (
          <OpenToButton
            artistId={profile.id!}
            userDisplayName={profile.displayName!}
            isOpenToWork={profile.openToWork}
          />
        )}
        {!isArtistMe && isUserLoggedIn && showArtistActions && (
          <>
            {isUserArtist(profile as User) && (
              <>
                {profile.openToWork && (
                  <HireArtistButton
                    artistId={profile.id!}
                    userDisplayName={profile.displayName!}
                  />
                )}
                <ArtistTipModalButton user={profile as User} />
              </>
            )}
            <ArtistFollowButton
              userId={profile.id!}
              isUserFollowing={!!isUserFollowing}
            />
          </>
        )}
      </section>
    </header>
  );
};

export default ProfileHeader;
export { ProfileHeaderBase };
